import { paramToNum, paramToString } from "../../basic/startup/BasicURLVariables";
import { HTMLEscaper } from "../../stringhelper/HTMLEscaper";

import { CampaignConstants } from "./CampaignConstants";
import { CampaignVarsVO } from "./CampaignVarsVO";

import { getLogger } from "@goodgamestudios/logger";

const logger = getLogger("CampaignVars");

/**
 * Parses and holds the campaign variables (also known as marekting variables campaign info).
 * These values allows it to keep track of advertisements e.g. Google AdWord or banner campaigns. In that way the business intelligence
 * can analyse which user requested the specific client through which campaign.
 *
 * See tracking documentation for details:
 * https://docs.google.com/a/goodgamestudios.com/spreadsheet/ccc?key=0AlN2EPXCQVModDB1Z2FiTlF6dHVnWjduN1drSUtYa1E#gid=128
 *
 * Where are the campaign vars used?
 * The data is send within the FirstInstanceTrackingEvent (Id 4) and the WorldAssignmentTrackingEvent (Id 41) and with the registration.
 * Actually the game server sends the registration event (Id 1) itself but the client passes the campaign vars to the game server in the
 * register commands. The BasicTrackingStringComposer is responsible to compose the campaign into the correct format.
 *
 * Why lacks the types of the parameters?
 * Hence the most parameters are type in the documentation it turns out that converting certain values in int causes overflows.
 * That's why all of the parameters are handled as simple Stings within the client!
 *
 * TODO: The default delimiter ";" has to be removed from every value while parsing the data!
 *
 */
export class CampaignVars {
    /** Delimiter for campaignVars **/
    public static DEFAULT_DELIMITER: string = ";";

    private _isInitialized: boolean = false;

    private _partnerId: string;
    private _lpPartnerId: string;
    // Affiliate
    private _channelId: string;
    private _trafficSource: string;
    // Google
    private _creative: string;
    // Placement may contain the URL of a site which contained e.g. a banner
    private _placement: string;
    private _keyword: string;
    private _network: string;
    private _lp: string;
    private _aid: string;
    private _camp: string;
    private _adgr: string;
    private _matchtype: string;
    private _lang: string;
    private _country: string;

    public initialize(campaignVarsVO: CampaignVarsVO): void {
        let vars: URLSearchParams = campaignVarsVO.data;

        logger.debug(
            "Client:" + campaignVarsVO.clientObjectClassName +
            ", context: " + campaignVarsVO.contextDescription +
            ", vars: " + campaignVarsVO.data +
            ", initialized: " + this._isInitialized
        );

        // Sanity check: vars object defined?
        if (!vars) {
            logger.debug("CampaignVars, initialize(): vars are null");
            return;
        }

        // Sanity check: Initialize once
        if (this._isInitialized) {
            return;
        }

        try {
            // Partner Id
            this._partnerId = this.parseAttribute(paramToString(vars.get(CampaignConstants.PARTNER_ID), "-1"));

            // LandinPpage Partner Id
            this._lpPartnerId = this.parseAttribute(paramToString(vars.get(CampaignConstants.LP_PARTNER_ID), "-1"));

            // Channel Id
            this._channelId = this.parseAttribute(paramToString(vars.get(CampaignConstants.CHANNEL_ID)));

            // Traffic source
            this._trafficSource = this.parseAttribute(paramToString(vars.get(CampaignConstants.TRAFFIC_SOURCE)));

            // Creative, default: -1
            let lCreative: number = paramToNum(vars.get(CampaignConstants.CREATIVE), -1);
            this._creative = lCreative > 0 ? this.parseAttribute(lCreative.toString()) : "-1";

            // Placement
            this._placement = this.parseAttribute(paramToString(vars.get(CampaignConstants.PLACEMENT)));

            // Keyword (must be sent as hex-string)
            this._keyword = this.convertToHexString(this.parseAttribute(paramToString(vars.get(CampaignConstants.KEYWORD))));

            // Network
            this._network = this.parseAttribute(paramToString(vars.get(CampaignConstants.NETWORK)));

            // Landingpage, default: -1
            let lLp: number = paramToNum(vars.get(CampaignConstants.LP), -1);
            this._lp = lLp > 0 ? this.parseAttribute(lLp.toString()) : "-1";

            // Ad Id, default: -1
            let lAid: number = paramToNum(vars.get(CampaignConstants.AID), -1);
            this._aid = lAid > 0 ? this.parseAttribute(lAid.toString()) : "-1";

            // Camp
            this._camp = this.parseAttribute(paramToString(vars.get(CampaignConstants.CAMP), "-1"));

            // Adgr
            this._adgr = this.parseAttribute(paramToString(vars.get(CampaignConstants.ADGR), "-1"));

            // Matchtype
            this._matchtype = this.parseAttribute(paramToString(vars.get(CampaignConstants.MATCHTYPE)));

            // Language
            this._lang = this.parseAttribute(paramToString(vars.get(CampaignConstants.LANGUAGE)));

            // Country
            this._country = this.parseAttribute(paramToString(vars.get(CampaignConstants.COUNTRY)));

            // If variables are valid, don't initialize twice
            if (this.isValid()) {
                this._isInitialized = true;
            }

            logger.debug("initialization done. Vars:" + toString());
        } catch (e) {
            logger.warn("Failed constructing campaign vars.");
        }
    }

    /**
     * The "keyword" attribute must be encoded and sent as a hex-string.
     * @param value
     * @return
     */

    private convertToHexString(value: string): string {
        let result: string = "";

        for (let i: number = 0; i < value.length; i++) {
            result += value.charCodeAt(i).toString(16);
        }

        return result;
    }

    /**
     * All attributes may contain several values divided by a "&percnt;" HTML-entity.
     * These and ";" have to be removed.
     *
     * @param attribute
     * @return
     */
    private parseAttribute(attribute: string): string {
        // safety unescape html entities, semicolon and other not allowed characters.
        return (attribute && attribute !== "") ? HTMLEscaper.unescape(attribute) : "";
    }

    public toObject(): object {
        // Create a new object
        let campaignVars: object = {};

        // Fill the object with values from VO
        campaignVars[CampaignConstants.PARTNER_ID] = this._partnerId;
        campaignVars[CampaignConstants.LP_PARTNER_ID] = this._lpPartnerId;
        campaignVars[CampaignConstants.CHANNEL_ID] = this._channelId;
        campaignVars[CampaignConstants.TRAFFIC_SOURCE] = this._trafficSource;
        campaignVars[CampaignConstants.CREATIVE] = this._creative;
        campaignVars[CampaignConstants.PLACEMENT] = this._placement;
        campaignVars[CampaignConstants.KEYWORD] = this._keyword;
        campaignVars[CampaignConstants.NETWORK] = this._network;
        campaignVars[CampaignConstants.LP] = this._lp;
        campaignVars[CampaignConstants.AID] = this._aid;
        campaignVars[CampaignConstants.CAMP] = this._camp;
        campaignVars[CampaignConstants.ADGR] = this._adgr;
        campaignVars[CampaignConstants.MATCHTYPE] = this._matchtype;
        campaignVars[CampaignConstants.LANGUAGE] = this._lang;
        campaignVars[CampaignConstants.COUNTRY] = this._country;

        return campaignVars;
    }

    public toString(): string {
        return [
            "partnerId: " + this.partnerId,
            "channelId: " + this.channelId,
            "trafficSource: " + this.trafficSource,
            "creative: " + this.creative,
            "placement: " + this.placement,
            "keyword: " + this.keyword,
            "network: " + this.network,
            "lp: " + this.lp,
            "aid: " + this.aid,
            "camp: " + this.camp,
            "adgr: " + this.adgr,
            "matchtype: " + this.matchtype,
            "lang: " + this.lang,
            "country: " + this.country
        ].join(", ");
    }

    public get isInitialized(): boolean {
        return this._isInitialized;
    }

    public isValid(): boolean {
        let valid: boolean;

        let pid: number = parseInt(this._partnerId);

        // The partner id (pid) is 0 (zero) when the user is coming from our landing page.
        // The campaign vars must be valid also for this case.
        if (!isNaN(pid) && pid >= 0) {
            valid = true;
        }

        return valid;
    }

    public get channelId(): string {
        return this._channelId || "";
    }

    public set channelId(value: string) {
        this._channelId = value;
    }

    public get trafficSource(): string {
        return this._trafficSource || "";
    }

    public set trafficSource(value: string) {
        this._trafficSource = value;
    }

    public get partnerId(): string {
        return this._partnerId || "-1";
    }

    public get lpPartnerID(): string {
        return this._lpPartnerId || "-1";
    }

    public get extendedPartnerID(): string {
        if (this.partnerId !== "-1") {
            return this.partnerId;
        } else if (this.lpPartnerID !== "-1") {
            return this.lpPartnerID;
        } else {
            return "-1";
        }
    }

    public get creative(): string {
        return this._creative || "-1";
    }

    public get placement(): string {
        return this._placement || "";
    }

    public get keyword(): string {
        return this._keyword || "";
    }

    public get network(): string {
        return this._network || "";
    }

    public get lp(): string {
        return this._lp || "-1";
    }

    //noinspection JSAnnotator
    public set lp(value: string) {
        this._lp = value;
    }

    public get aid(): string {
        return this._aid || "-1";
    }

    public get camp(): string {
        return this._camp || "-1";
    }

    public get adgr(): string {
        return this._adgr || "-1";
    }

    public get matchtype(): string {
        return this._matchtype || "";
    }

    public get lang(): string {
        return this._lang || "";
    }

    public get country(): string {
        return this._country || "";
    }
}
